import { Dayjs } from "dayjs";
import { sorted } from "helpers";
import {
  EventLabellingModelStatus,
  GetEventsForReviewApiArg,
} from "src/store/api/generatedApi";

// RTK Query keys its cache on the serialised args, so equal exclusion sets must produce
// equal arrays or two callers asking the same question would fetch twice.
const periodIdArgument = (periodIds: ReadonlySet<number>): number[] =>
  sorted([...periodIds], (periodId) => periodId);

/**
 * Query arguments for a review-events fetch. Every caller builds them here so that a
 * prefetch, a bring-in modal and an adjacent-period preview asking for the same window
 * and the same exclusions land in one cache entry.
 *
 * The exclusion sets are the labelling periods to keep out of the merge, and are required:
 * passing none merges every labeller, including the ones a `DefaultTimelineExclusion`
 * covers, which is only right before a surface has learnt what its window contains.
 */
export const reviewEventsQueryArgs = ({
  tenantName,
  location,
  windowStart,
  windowEnd,
  excludedPeriodIds,
  excludeMaterialPeriodIds,
  excludeTimingPeriodIds,
  includeVerified,
  omittedModelStatuses,
}: {
  tenantName: string;
  location: string;
  windowStart: Dayjs;
  windowEnd: Dayjs;
  excludedPeriodIds: ReadonlySet<number>;
  excludeMaterialPeriodIds: ReadonlySet<number>;
  excludeTimingPeriodIds: ReadonlySet<number>;
  includeVerified?: boolean;
  omittedModelStatuses?: readonly EventLabellingModelStatus[];
}): GetEventsForReviewApiArg => ({
  tenantName,
  windowStart: windowStart.toISOString(),
  windowEnd: windowEnd.toISOString(),
  location,
  excludedPeriodIds: periodIdArgument(excludedPeriodIds),
  excludeMaterialPeriodIds: periodIdArgument(excludeMaterialPeriodIds),
  excludeTimingPeriodIds: periodIdArgument(excludeTimingPeriodIds),
  includeVerified,
  ...(omittedModelStatuses?.length
    ? {
        omittedModelStatuses: sorted(
          [...omittedModelStatuses],
          (status) => status
        ),
      }
    : {}),
});
